<template>
  <div class="result_page">
    <van-nav-bar
      title="体质报告"
      left-arrow
      @click-left="onClickLeft"
      fixed
      placeholder
      safe-area-inset-top
    />

    <!-- 页面内的全屏加载提示 -->
    <div class="page_loading" v-if="!resultData && isLoading">
      <van-loading type="spinner" color="#c9c9c9" vertical class="analyze-loading">正在分析中...</van-loading>
    </div>

    <!-- 当没有数据且不处于加载状态时，显示空状态 -->
    <div v-else-if="!resultData && !isLoading" style="padding-top: 100px;">
      <van-empty description="暂无分析结果" />
    </div>

    <template v-if="resultData">
      <!-- 顶部蓝色背景 -->
      <div class="top_blue_bg">
        <div class="white_card">
          <!-- 半透明蓝色信息条 -->
          <div class="info_bar">
            <div v-if="headerView.person" class="badge">{{headerView.person}}</div>
            <div v-if="headerView.gender" class="info_text">{{headerView.gender}}</div>
            <div v-if="headerView.ageText" class="info_text">{{headerView.ageText}}</div>
            <div v-if="headerView.heightText" class="info_text">{{headerView.heightText}}</div>
            <div v-if="headerView.weightText" class="info_text">{{headerView.weightText}}</div>
          </div>
          <!-- 体质描述 -->
          <div class="desc_text">
            多数人的体质特征是不典型的，兼夹体质者为多，根据您的本次检测结果分析，主要体质结果如下
          </div>
          <!-- 大标题：体质 -->
          <div v-if="mainConstitution" class="big_constitution">
            {{ mainConstitution }}
          </div>
          <!-- 小说明 -->
          <div class="small_hint">
            调理时建议以主体质为主，兼夹为辅
          </div>
          <div v-if="mixedConstitution" class="mixed_row">
            <span class="mixed_label">兼有体质</span>
            <span class="mixed_value">{{ mixedConstitution }}</span>
          </div>
          <div class="divider"></div>
          <div v-if="healthMetrics.length" class="metrics_row">
            <div
              v-for="metric in healthMetrics"
              :key="metric.key"
              class="metric_item"
            >
              <div class="metric_label">{{ metric.label }}</div>
              <div class="metric_value">{{ metric.value }}</div>
              <div
                class="metric_status"
                :class="getMetricStatusClass(metric)"
              >
                {{ metric.status }}
              </div>
            </div>
          </div>

          <!-- 删除了原先的面诊报告和查看答题记录按钮，恢复至17:00前状态 -->
        </div>

        <!-- 答题记录栏 -->
        <div class="record_bar" @click="goToQaRecord">
          <div class="record_link">
            <span>答题记录</span>
            <van-icon name="arrow" />
          </div>
          <div>{{ resultData.createTime || '2026年4月3日 18:28:37' }}</div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="tabs_wrap">
        <van-tabs v-model="activeTab" color="#2F6BFF" @change="onTabChange">
          <van-tab title="表现症状">
            <div class="section">
              <div class="section_title">总体特征</div>
              <div class="section_text">{{resultData.generalCharacteristics || '暂无'}}</div>
            </div>
            <div class="section">
              <div class="section_title">发病倾向</div>
              <div class="section_text">{{resultData.diseaseTrend || '暂无'}}</div>
            </div>
            <div class="section">
              <div class="section_title">常见表现</div>
              <div class="section_text">{{resultData.commonManifestation || '暂无'}}</div>
            </div>
            <div class="section">
              <div class="section_title">调理思路</div>
              <div class="section_text">{{resultData.treatmentPlan || '暂无'}}</div>
            </div>

            <!-- 面诊/舌诊报告入口 -->
            <div class="report_entry_list" style="margin-top: 16px;">
              <div class="report_entry_card face_card" @click="goTo('/web/my-ai/face-diagnosis')">
                <div class="entry_icon">
                  <img
                    src="https://pro-yeahgo-oss.yeahgo.com/file/zywzimg/面部详情.png"
                    alt="面诊报告"
                    class="entry_icon_img"
                  />
                </div>
                <div class="entry_text">面诊报告详情</div>
                <van-icon name="arrow" class="entry_arrow" />
              </div>
              <div class="report_entry_card tongue_card" @click="goTo('/web/my-ai/tongue-diagnosis')">
                <div class="entry_icon">
                  <img
                    src="https://pro-yeahgo-oss.yeahgo.com/file/zywzimg/舌头详情.png"
                    alt="舌诊报告"
                    class="entry_icon_img"
                  />
                </div>
                <div class="entry_text">舌诊报告详情</div>
                <van-icon name="arrow" class="entry_arrow" />
              </div>
              <div class="report_entry_card pulse_card" @click="goTo('/web/my-ai/pulse-diagnosis')">
                <div class="entry_icon">
                  <img
                    src="https://pro-yeahgo-oss.yeahgo.com/file/zywzimg/脉诊详情.png"
                    alt="脉诊及体征报告"
                    class="entry_icon_img"
                  />
                </div>
                <div class="entry_text">脉诊及体征报告</div>
                <van-icon name="arrow" class="entry_arrow" />
              </div>
            </div>
          </van-tab>
          <van-tab title="调理建议">
            <div class="section">
              <div class="advice_title_row">
                <div class="advice_title">调理建议</div>
              </div>
              <div class="advice_sub">{{ (resultData && resultData.treatmentSuggestion) || '暂无' }}</div>
            </div>

            <!-- 食疗建议 -->
            <div class="section">
              <div class="advice_title_row">
                <div class="advice_title">食疗建议</div>
              </div>
              <div class="advice_sub">{{ (resultData && resultData.dietSuggestion) || '暂无' }}</div>
            </div>
            <!-- 宜（蓝色浅底卡片） -->
            <div class="tint_card tint_blue">
              <div class="tint_icon tint_icon_blue">宜</div>
              <div class="tint_text">{{ (resultData && resultData.recommend) || '暂无' }}</div>
            </div>
            <!-- 忌（红色浅底卡片） -->
            <div class="tint_card tint_red">
              <div class="tint_icon tint_icon_red">忌</div>
              <div class="tint_text">{{ (resultData && resultData.avoid) || '暂无' }}</div>
            </div>
            <!-- 调理推荐（商品） -->
            <div v-if="resultData && resultData.goodsList && resultData.goodsList.length" class="section">
              <div class="advice_title_row">
                <div class="advice_title">调理推荐</div>
              </div>
              <div class="advice_sub">{{ (resultData && resultData.dietSuggestion) || '' }}</div>
              <div class="goods_list">
                <template v-for="(item, index) in resultData.goodsList" :key="index">
                  <div class="promo_card" @click="onGoodsClick(item)">
                    <img class="promo_img" :src="item.image || item.img || item.picUrl || item.url" />
                    <div style="flex:1; position:relative">
                      <div class="promo_title van-multi-ellipsis--l3">{{item.title || item.name}}</div>
                      <div style="position:absolute; bottom:5px;width:100%">
                        <div class="promo_marketPrice">市场价<span style="text-decoration: line-through;">¥{{(item.marketPrice || item.market_price || 0)/100}}</span></div>
                        <div class="promo_price_row">
                          <div class="promo_salePrice"><span style="font-size:12px">¥</span>{{(item.salePrice || item.sale_price || item.price || 0)/100}}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </van-tab>
        </van-tabs>
      </div>
      <div class="important_statement">
        重要声明：本报告采用AI人工智能分析总结，仅供参考，不可作为医疗诊断结论，如已有身体不适，请及时咨询医生。
      </div>
    </template>
  </div>
</template>

<script>
import { post } from '@/utils/request';
import { Loading, Tabs, Tab, Toast, Button, Empty, Icon, NavBar } from 'vant';

export default {
  name: 'MyAiResult',
  components: {
    [Loading.name]: Loading,
    [Tabs.name]: Tabs,
    [Tab.name]: Tab,
    [Button.name]: Button,
    [Empty.name]: Empty,
    [Icon.name]: Icon,
    [NavBar.name]: NavBar,
  },
  data() {
    return {
      isLoading: true,
      payload: null,
      reportId: null,
      resultData: null,
      activeTab: 0,
      headerView: {
        person: "",
        gender: "",
        ageText: "",
        heightText: "",
        weightText: ""
      }
    };
  },
  computed: {
    pulseReport() {
      return this.resultData?.pulseReport || null;
    },
    mainConstitution() {
      return this.resultData?.constitution || this.parsedDiagnosisConstitution.main || '';
    },
    mixedConstitution() {
      return this.resultData?.constitution1 || this.parsedDiagnosisConstitution.mixed || '';
    },
    parsedDiagnosisConstitution() {
      const resultText = this.pulseReport?.diagnosisResult?.result || '';
      const matched = resultText.match(/^(.+?)兼(.+?)(?:，|。|；|,|$)/);
      if (!matched) {
        return { main: '', mixed: '' };
      }
      return {
        main: matched[1] || '',
        mixed: matched[2] || '',
      };
    },
    healthMetrics() {
      const indicatorList = this.pulseReport?.indicatorAnalysis?.list || [];
      const input = this.pulseReport?.input || {};

      const sbpItem = this.getIndicatorByKey('sbp', indicatorList);
      const dbpItem = this.getIndicatorByKey('dbp', indicatorList);
      const hrItem = this.getIndicatorByKey('hr', indicatorList);
      const spo2Item = this.getIndicatorByKey('spo2', indicatorList);

      const bloodPressureValue = this.formatBloodPressureValue(sbpItem, dbpItem, input);
      const bloodPressureMeta = this.pickBloodPressureMeta(sbpItem, dbpItem);

      return [
        {
          key: 'hr',
          label: '平均心率',
          value: this.formatHeartRateValue(hrItem, input),
          status: hrItem?.status || '--',
          riskLevel: hrItem?.riskLevel || '',
        },
        {
          key: 'bloodPressure',
          label: '血压',
          value: bloodPressureValue,
          status: bloodPressureMeta?.status || '--',
          riskLevel: bloodPressureMeta?.riskLevel || '',
        },
        {
          key: 'spo2',
          label: '血氧',
          value: this.formatSpo2Value(spo2Item, input),
          status: spo2Item?.status || '--',
          riskLevel: spo2Item?.riskLevel || '',
        }
      ].filter(metric => metric.value !== '--' || metric.status !== '--');
    }
  },
  created() {
    let reportId = this.$route.query.reportId;
    
    // 如果 URL 中没有，则尝试从 sessionStorage (上个页面传值) 中获取
    if (!reportId) {
      const payloadStr = sessionStorage.getItem("MY_AI_SUBMIT_PAYLOAD") || localStorage.getItem("MY_AI_SUBMIT_PAYLOAD");
      if (payloadStr) {
        try {
          const payload = JSON.parse(payloadStr);
          reportId = payload.reportId;
        } catch (e) {
          console.error("解析缓存数据失败", e);
        }
      }
    }

    if (reportId) {
      this.reportId = reportId;
      this.payload = { reportId };
      this.fetchReportData(reportId);
    } else {
      this.isLoading = false;
      Toast({ message: "缺少报告ID", position: 'middle' });
    }
  },
  methods: {
    getIndicatorByKey(key, list = []) {
      return list.find(item => item?.keys === key) || null;
    },
    cleanNumericText(value) {
      if (value === undefined || value === null || value === '') return '';
      return String(value).replace(/\s+/g, '');
    },
    formatHeartRateValue(item, input = {}) {
      if (input.hr !== undefined && input.hr !== null && input.hr !== '') {
        return `${input.hr}bpm`;
      }
      const text = this.cleanNumericText(item?.result);
      if (!text) return '--';
      return text
        .replace(/次\/分/g, 'bpm')
        .replace(/次\/分钟/g, 'bpm');
    },
    formatSpo2Value(item, input = {}) {
      if (input.spo2 !== undefined && input.spo2 !== null && input.spo2 !== '') {
        return `${input.spo2}%`;
      }
      const text = this.cleanNumericText(item?.result);
      if (!text) return '--';
      return text.replace(/％/g, '%');
    },
    formatBloodPressureValue(sbpItem, dbpItem, input = {}) {
      const sbp = input.sbp || this.extractLeadingNumber(sbpItem?.result);
      const dbp = input.dbp || this.extractLeadingNumber(dbpItem?.result);
      if (sbp && dbp) {
        return `${sbp}/${dbp}mmHg`;
      }
      return '--';
    },
    extractLeadingNumber(value) {
      const match = String(value || '').match(/-?\d+(\.\d+)?/);
      return match ? match[0] : '';
    },
    pickBloodPressureMeta(sbpItem, dbpItem) {
      if (!sbpItem && !dbpItem) return null;
      if (!sbpItem) return dbpItem;
      if (!dbpItem) return sbpItem;

      const sbpLevel = this.getRiskWeight(sbpItem.riskLevel, sbpItem.status);
      const dbpLevel = this.getRiskWeight(dbpItem.riskLevel, dbpItem.status);
      return sbpLevel >= dbpLevel ? sbpItem : dbpItem;
    },
    getRiskWeight(riskLevel, status) {
      const riskMap = {
        '低': 1,
        '中低': 2,
        '中': 3,
        '中高': 4,
        '高': 5,
      };
      if (riskMap[riskLevel]) return riskMap[riskLevel];
      if (!status || status === '正常') return 1;
      if (status.includes('重度') || status.includes('严重')) return 5;
      if (status.includes('中度')) return 4;
      if (status.includes('轻度')) return 3;
      if (status.includes('偏高') || status.includes('偏低') || status.includes('高') || status.includes('低')) return 2;
      return 1;
    },
    getMetricStatusClass(metric) {
      const level = this.getRiskWeight(metric?.riskLevel, metric?.status);
      if (level >= 4) return 'is-danger';
      if (level >= 2) return 'is-warn';
      return 'is-normal';
    },
    onClickLeft() {
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        // 如果没有历史记录（例如直接输入URL），可以默认返回首页或特定页面
        this.$router.push('/');
      }
    },
    fetchReportData(reportId) {
      post({
        url: "/healthy/auth/consultation/report",
        data: { reportId },
      })
        .then((res) => {
          const result = res?.data || res || null;
          this.resultData = result;
          this.headerView = this.buildHeaderView(result);
          this.isLoading = false;
        })
        .catch(() => {
          this.isLoading = false;
          Toast({ message: "获取报告失败", position: 'middle' });
        });
    },
    buildHeaderView(result) {
      const person = result?.userName || result?.person || "";
      const gender = result?.gender || "";
      const height = result?.height ? `${parseFloat(result.height)}cm` : "";
      const weight = result?.weight ? `${parseFloat(result.weight)}kg` : "";
      let ageText = "";
      const birthday = result?.birthday;
      if (birthday) {
        try {
          const birth = new Date(birthday.replace(/-/g, "/"));
          const now = new Date();
          let age = now.getFullYear() - birth.getFullYear();
          const m = now.getMonth() - birth.getMonth();
          if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
          if (age > 0) ageText = `${age}岁`;
        } catch (e) {
          console.error(e);
        }
      }
      return { person, gender, ageText, heightText: height, weightText: weight };
    },
    onGoodsClick(item) {
      const actionUrl = item.actionUrl || item.action_url || "";
      if (item.wxAppId) {
        if (window.wx && window.wx.miniProgram) {
           window.wx.miniProgram.navigateTo({
             url: actionUrl
           });
        } else {
           Toast("请在微信小程序中打开");
        }
      } else {
        if(actionUrl.startsWith('http')) {
          window.location.href = actionUrl;
        } else if (actionUrl) {
          this.$router.push(actionUrl);
        }
      }
    },
    goTo(path) {
      if (this.reportId) {
        this.$router.push({ path, query: { ...this.$route.query, reportId: this.reportId } });
      } else {
        Toast("未获取到报告ID");
      }
    },
    onTabChange(index) {
      this.activeTab = index;
    },
    goToQaRecord() {
      // 透传 chat 数组到 session 缓存，避免 URL 过长
      if (this.resultData && this.resultData.chat) {
        sessionStorage.setItem("MY_AI_QA_RECORD", JSON.stringify(this.resultData.chat));
      }
      this.goTo('/web/my-ai/qa-record');
    }
  }
};
</script>

<style lang="scss" scoped>
.result_page {
  background: #f7f8fa;
  min-height: 100vh;
}

.page_loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 80vh;

  ::v-deep .van-loading__spinner {
    width: 36px;
    height: 36px;
  }
  ::v-deep .van-loading__text {
    font-size: 16px;
  }
}

.top_blue_bg {
  width: 100%;
  background: #2F6BFF;
  padding: 16px 0;
}

.white_card {
  margin: 0 16px;
  background: #fff;
  border-radius: 12px;
  padding: 0 0 16px;
}

.info_bar {
  height: 40px;
  background: rgba(47, 107, 255, 0.2);
  border-radius: 8px;
  margin: 8px 0 16px 0;
  padding: 0 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.badge {
  background: #2F6BFF;
  color: #fff;
  font-size: 14px;
  padding: 3px 8px;
  border-radius: 999px;
}

.info_text {
  color: #2F6BFF;
  font-size: 15px;
}

.desc_text {
  color: #333;
  font-size: 16px;
  line-height: 1.6;
  padding: 0 10px;
  margin-bottom: 16px;
}

.big_constitution {
  text-align: center;
  color: #2F6BFF;
  font-size: 31px;
  font-weight: 600;
  margin: 30px 0 20px 0;
}

.small_hint {
  text-align: center;
  color: #999;
  font-size: 15px;
  margin-bottom: 24px;
  padding: 0 10px;
}

.divider {
  height: 1px;
  background: #cccccc;
  margin: 12px 0 0;
  transform: scaleY(0.5);
}

.mixed_row {
  display: flex;
  align-items: center;
  min-height: 40px;
  padding: 0 10px;
  margin-bottom: 8px;
}
.mixed_label {
  color: #999999;
  font-size: 15px;
  margin-right: 8px;
}
.mixed_value {
  color: #8B5E3C;
  font-size: 16px;
}

.metrics_row {
  display: flex;
  padding: 18px 0 6px;
}

.metric_item {
  flex: 1;
  text-align: center;
  padding: 0 8px;
}

.metric_label {
  color: #333;
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  margin-bottom: 4px;
}

.metric_value {
  color: #333;
  font-size: 15px;
  font-weight: 400;
  line-height: 21px;
  margin-bottom: 3px;
  word-break: break-word;
}

.metric_status {
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
}

.metric_status.is-normal {
  color: #1ecb44;
}

.metric_status.is-warn {
  color: #ff7a00;
}

.metric_status.is-danger {
  color: #ff4d4f;
}

.record_bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px 0;
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
}
.record_link {
  display: flex;
  align-items: center;
  gap: 2px;
}

.tabs_wrap {
  padding: 12px 12px 20px;
}

/* 面诊报告按钮样式 */
.report_entry_list {
  padding: 0 12px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.report_entry_card {
  display: flex;
  align-items: center;
  height: 60px;
  border-radius: 8px;
  padding: 0 20px;
  background: #711982; /* 默认面诊紫色背景，可被覆盖 */
  color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
.face_card {
  background: linear-gradient(90deg, #781c8b 0%, #87219c 100%);
}
.tongue_card {
  background: linear-gradient(90deg, #1c7c54 0%, #229364 100%);
}
.pulse_card {
  background: linear-gradient(90deg, #1332f0 0%, #1d39ff 100%);
}
.entry_icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
}
.entry_icon_img {
  width: 24px;
  height: 24px;
  object-fit: contain;
  display: block;
}
.entry_text {
  flex: 1;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 1px;
}
.entry_arrow {
  font-size: 16px;
  opacity: 0.8;
}

.important_statement {
  padding: 0 20px 24px;
  color: #999;
  font-size: 14px;
  line-height: 1.6;
}

.section {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  margin: 8px 0;
}
.section_title { font-size: 16px; color: #333; font-weight: 600; margin-bottom: 6px; }
.section_text { font-size: 15px; color: #666; line-height: 1.6; }

.advice_title_row { margin-bottom: 4px; }
.advice_title { font-size: 16px; color: #333; font-weight: 600; }
.advice_sub { font-size: 14px; color: #666; }

.tint_card {
  border-radius: 8px;
  padding: 12px;
  margin: 8px 0;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
}
.tint_blue { background: #F0F5FF; }
.tint_red { background: #FFF2F2; }
.tint_icon {
  width: 28px; height: 28px;
  border-radius: 6px;
  text-align: center; line-height: 28px;
  color: #fff; font-size: 14px; font-weight: 600;
  margin-right: 8px;
  flex-shrink: 0;
}
.tint_icon_blue { background: #2F6BFF; }
.tint_icon_red { background: #FF4D4F; }
.tint_text { font-size: 15px; color: #666; line-height: 1.8; flex: 1; }

.goods_list { margin-top: 8px; display: flex; flex-direction: column; gap: 8px; }
.promo_card {
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  position: relative;
  padding: 0;
  gap: 10px;
}
.promo_img {
  width: 150px;
  height: 150px;
  flex-shrink: 0;
  margin-left: 0;
  border-radius: 6px;
  object-fit: cover;
  margin: 0 10px 0 0;
}
.promo_title { color: #333; font-size: 15px; line-height: 1.5; }
.promo_marketPrice { color: #939293; margin-bottom: 8px; font-size: 12px; }
.promo_marketPrice span { text-decoration: line-through; }
.promo_salePrice { color: #FF000B; font-weight: bold; font-size: 20px; }
.promo_price_row { display: flex; justify-content: flex-start; align-items: center; }

.bottom-button {
  width: 160px;
  height: 40px;
}
</style>
